import { createTheme } from '@mui/material/styles';
import type {} from '@mui/x-data-grid/themeAugmentation';
import type {} from '@mui/x-date-pickers/themeAugmentation';

// Kept in sync with Receipt-Desktop/src/theme.ts so both apps share one look.
export const TRUE_COLORS = {
  primary:   '#14252C',
  primaryHover: '#1e3540',
  secondary: '#6F8994',
  beige:     '#A3A39B',
  lightBg:   '#f5f7fa',
  white:     '#FFFFFF',
  border:    'rgba(0, 0, 0, 0.05)',
  borderSolid: '#e0e0e0',
  shadow:    '0 2px 8px rgba(0, 0, 0, 0.05)',
  shadowHover: '0 4px 16px rgba(0, 0, 0, 0.08)',
  headerGradient: 'linear-gradient(135deg, #0a1419 0%, #0d1b2a 50%, #0a1419 100%)',
  success: '#2e7d5b',
  danger:  '#c0392b',
  warning: '#b7791f',
};

// Ordered series palette for charts: brand dark first, then muted brand tones.
export const CHART_COLORS = ['#14252C', '#6F8994', '#C2A878', '#9BB7C1', '#3E6A5C', '#A3A39B', '#D08C60'];

export const TRUE_LOGO_BLACK = `${process.env.PUBLIC_URL}/logo-negro.svg`;
export const TRUE_LOGO_WHITE = `${process.env.PUBLIC_URL}/imagotipo-blanco.svg`;

export const SIDEBAR_WIDTH = 240;
export const APPBAR_HEIGHT = 72;
export const CONTENT_MAX_WIDTH = 1440;

const fontFamily = 'Inter, -apple-system, BlinkMacSystemFont, sans-serif';

const theme = createTheme({
  palette: {
    primary:   { main: TRUE_COLORS.primary },
    secondary: { main: TRUE_COLORS.secondary },
    success:   { main: TRUE_COLORS.success },
    error:     { main: TRUE_COLORS.danger },
    warning:   { main: TRUE_COLORS.warning },
    background: { default: TRUE_COLORS.lightBg, paper: TRUE_COLORS.white },
    text: { primary: TRUE_COLORS.primary, secondary: TRUE_COLORS.secondary },
    divider: TRUE_COLORS.borderSolid,
  },
  typography: {
    fontFamily,
    h1: { fontWeight: 700 },
    h2: { fontWeight: 700 },
    h3: { fontWeight: 700 },
    h4: { fontWeight: 700, fontSize: '1.75rem', letterSpacing: '-0.01em' },
    h5: { fontWeight: 600, fontSize: '1.25rem' },
    h6: { fontWeight: 600, fontSize: '1rem' },
    subtitle1: { fontWeight: 600 },
    subtitle2: { fontWeight: 600, fontSize: '0.8125rem' },
    overline: { fontWeight: 700, letterSpacing: '0.08em', fontSize: '0.6875rem' },
    button: { textTransform: 'none', fontWeight: 600 },
  },
  shape: { borderRadius: 8 },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: { backgroundColor: TRUE_COLORS.lightBg },
      },
    },
    MuiButton: {
      defaultProps: { disableElevation: true },
      styleOverrides: {
        root: { borderRadius: 8, paddingInline: 16 },
        containedPrimary: {
          backgroundColor: TRUE_COLORS.primary,
          '&:hover': { backgroundColor: TRUE_COLORS.primaryHover, boxShadow: '0 4px 12px rgba(0,0,0,0.15)' },
        },
        outlined: { borderColor: TRUE_COLORS.borderSolid },
      },
    },
    MuiIconButton: {
      styleOverrides: { root: { borderRadius: 8 } },
    },
    MuiTextField: {
      defaultProps: { variant: 'outlined', size: 'small' },
    },
    MuiFormControl: {
      defaultProps: { size: 'small' },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          backgroundColor: TRUE_COLORS.white,
          '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: TRUE_COLORS.secondary },
        },
        notchedOutline: { borderColor: TRUE_COLORS.borderSolid },
      },
    },
    MuiCard: {
      defaultProps: { elevation: 0 },
      styleOverrides: {
        root: {
          boxShadow: TRUE_COLORS.shadow,
          border: `1px solid ${TRUE_COLORS.borderSolid}`,
          borderRadius: 12,
        },
      },
    },
    MuiCardHeader: {
      styleOverrides: {
        root: { padding: '20px 24px 0' },
        title: { fontSize: '1rem', fontWeight: 600, color: TRUE_COLORS.primary },
        subheader: { fontSize: '0.8125rem', color: TRUE_COLORS.secondary },
      },
    },
    MuiCardContent: {
      styleOverrides: {
        root: { padding: 24, '&:last-child': { paddingBottom: 24 } },
      },
    },
    MuiPaper: {
      styleOverrides: { root: { backgroundImage: 'none' } },
    },
    MuiDialog: {
      styleOverrides: { paper: { borderRadius: 12 } },
    },
    MuiDialogTitle: {
      styleOverrides: { root: { fontWeight: 700, fontSize: '1.125rem' } },
    },
    MuiDrawer: {
      styleOverrides: { paper: { borderRight: 'none' } },
    },
    MuiMenu: {
      styleOverrides: {
        paper: { borderRadius: 10, border: `1px solid ${TRUE_COLORS.borderSolid}`, boxShadow: '0 4px 20px rgba(0,0,0,0.12)' },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 500,
          minHeight: 44,
          color: TRUE_COLORS.secondary,
          '&.Mui-selected': { color: TRUE_COLORS.primary, fontWeight: 600 },
        },
      },
    },
    MuiTabs: {
      styleOverrides: {
        root: { minHeight: 44, borderBottom: `1px solid ${TRUE_COLORS.borderSolid}` },
        indicator: { backgroundColor: TRUE_COLORS.primary, height: 2.5, borderRadius: 2 },
      },
    },
    MuiTableHead: {
      styleOverrides: {
        root: {
          '& .MuiTableCell-root': {
            backgroundColor: TRUE_COLORS.lightBg,
            color: TRUE_COLORS.secondary,
            fontWeight: 600,
            fontSize: '0.75rem',
            textTransform: 'uppercase',
            letterSpacing: '0.05em',
          },
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: { color: TRUE_COLORS.primary, borderBottom: `1px solid ${TRUE_COLORS.borderSolid}` },
      },
    },
    MuiChip: {
      styleOverrides: { root: { fontWeight: 500, borderRadius: 6 } },
    },
    MuiDivider: {
      styleOverrides: { root: { borderColor: TRUE_COLORS.borderSolid } },
    },
    MuiListItemButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          '&.Mui-selected': { backgroundColor: 'rgba(20,37,44,0.08)' },
          '&.Mui-selected:hover': { backgroundColor: 'rgba(20,37,44,0.12)' },
        },
      },
    },
    MuiTooltip: {
      styleOverrides: { tooltip: { backgroundColor: TRUE_COLORS.primary, fontSize: '0.75rem' } },
    },
    MuiAlert: {
      styleOverrides: { root: { borderRadius: 8 } },
    },
    MuiDataGrid: {
      defaultProps: { density: 'standard', disableRowSelectionOnClick: true },
      styleOverrides: {
        root: {
          border: 'none',
          '--DataGrid-containerBackground': TRUE_COLORS.lightBg,
          '& .MuiDataGrid-columnHeaderTitle': {
            fontWeight: 600, fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.05em', color: TRUE_COLORS.secondary,
          },
          '& .MuiDataGrid-cell': { borderColor: TRUE_COLORS.borderSolid },
          '& .MuiDataGrid-cell:focus, & .MuiDataGrid-columnHeader:focus': { outline: 'none' },
          '& .MuiDataGrid-row:hover': { backgroundColor: 'rgba(20,37,44,0.04)' },
        },
      },
    },
  },
});

export default theme;
